<script setup lang="ts">
import { useRefereshProductStore } from '@/products/commands/refreshProductStore';
import { useRefreshStorageAreaStore } from '@/storageAreas/stores/refreshStorageAreaStore';
import { useRefereshProductGroupStore } from '@/products/groups/commands/refreshProductGroupStore';
import { storeToRefs } from 'pinia';
import { DataTable, Column } from 'primevue';
import { useProductStore } from 'shared/products/stores/productStore';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import { useTranslations } from 'shared/translations/composables/useTranslations';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { computed, onMounted, onUnmounted, ref } from 'vue';

const storageAreaStore = useStorageAreaStore();
const storageAreas = storeToRefs(storageAreaStore).storageAreas;
const productStore = useProductStore();
const refreshProductStore = useRefereshProductStore();
const translations = useTranslations();
const languageStore = useLanguageStore();
const refreshStorageAreaStore = useRefreshStorageAreaStore();
const refreshProductGroupStore = useRefereshProductGroupStore();
const { loading } = storeToRefs(refreshStorageAreaStore);
const products = ref<ProductTableRow[]>([]);
const intervalId = ref<number | null>(null);

onMounted(async () => {
  await refreshStorageAreaStore.initialize();
  await refreshProductStore.initialize();
  await refreshProductGroupStore.initialize();

  updateData();

  intervalId.value = window.setInterval(async () => {
    updateData();
  }, 10000);
});

onUnmounted(() => {
  if (intervalId.value) {
    clearInterval(intervalId.value);
  }
});

function updateData() {
  products.value = [];
  // Loopar igenom alla lagringsområden
  for (const storageArea of storageAreas.value) {
    // Om lagringsområdet inte har logYardData, hoppa över detta område
    if (!storageArea.logYardData) continue;

    // Loopar igenom varje innehåll (content) i logYardData för lagringsområdet
    for (const content of storageArea.logYardData.content) {
      // Hitta produkten i produktlagret som matchar content.productId
      const product = productStore.products.find((p) => p.id === content.productId);
      // Om produkten inte finns i produktlagret, hoppa över denna iteration
      if (!product) continue;
      // Sätt default namn
      let productName = translations.value.generic.unknown;

      // Om produkten finns, använd dess riktiga namn
      if (product) {
        productName = product.name;
      }
      // Kolla om produkten finns
      const existing = products.value.find((p) => p.productName === productName);
      if (existing) {
        // Om produkten redan finns, addera antalet stockar och volymen
        existing.numberOfLogs += content.numberOfLogs;
        existing.volume += content.volume;
      } else {
        // Om produkten inte finns, skapa ett nytt objekt och lägg till i listan
        products.value.push({
          productName: productName,
          numberOfLogs: content.numberOfLogs,
          volume: content.volume,
        });
      }
    }
  }

  products.value = products.value.sort((a, b) => {
    if (a.productName === translations.value.generic.unknown) return 1; // "a" hamnar efter "b"
    if (b.productName === translations.value.generic.unknown) return -1; // "b" hamnar efter "a"
    return a.productName.localeCompare(b.productName); // alfabetisk sortering
  });
}

interface ProductTableRow {
  productName: string;
  numberOfLogs: number;
  volume: number;
}

function getVolume(row: ProductTableRow) {
  return languageStore.formatNumber(row.volume, 1);
}

function getNumberOfLogs(row: ProductTableRow) {
  return languageStore.formatNumber(row.numberOfLogs, 0);
}

const totalLogs = computed(() => {
  return languageStore.formatNumber(
    products.value.reduce((sum, row) => sum + row.numberOfLogs, 0),
    0,
  );
});

const totalVolume = computed(() => {
  return languageStore.formatNumber(
    products.value.reduce((sum, row) => sum + row.volume, 0),
    0,
  );
});
</script>

<template>
  <DataTable
    class="product-table"
    :value="products"
    data-key="id"
    scrollable
    :loading="loading"
    scrollHeight="flex"
    stripedRows
  >
    <template #header>
      <div class="flex flex-wrap items-center justify-between gap-2">
        <span class="text-xl font-bold">{{ translations.dashboard.productTable.title }}</span>
      </div>
    </template>
    <template #empty>{{ translations.generic.noData }}</template>
    <template #loading>{{ translations.generic.loadingTableData }}</template>
    <Column field="productName" :header="translations.generic.name" sortable>
      <template #footer>
        <strong>{{ translations.generic.total }}</strong>
      </template>
    </Column>
    <Column :field="getNumberOfLogs" :header="translations.generic.numberOfLogs" sortable>
      <template #footer>
        <strong>{{ totalLogs }}</strong>
      </template>
    </Column>
    <Column :field="getVolume" :header="translations.generic.volume" sortable>
      <template #footer>
        <strong>{{ totalVolume }}</strong>
      </template>
    </Column>
  </DataTable>
</template>

<style scoped>
:deep(.p-datatable-header div) {
  display: grid;
  grid-template-columns: auto 1fr auto auto;
}
</style>
